/**
 * The three shapes every side-by-side step renders its data in.
 *
 * @module components/portfolio/comparison/side-by-side-values
 */

import React from "react";

import { MutedText } from "@/components/ui";
import type { Module } from "@/types";

export function ValueList({
  items,
  empty,
}: {
  items: { id: string; title: string; detail?: string }[];
  empty: string;
}): React.ReactElement {
  if (items.length === 0) {
    return <MutedText className="py-6">{empty}</MutedText>;
  }
  return (
    <ul className="divide-y divide-base-300 border-y border-base-300">
      {items.map((item) => (
        <li key={item.id} className="py-3">
          <div className="font-medium">{item.title}</div>
          {item.detail ? <MutedText className="mt-1">{item.detail}</MutedText> : null}
        </li>
      ))}
    </ul>
  );
}

export function ModuleList({
  modules,
  detail,
}: {
  modules: readonly Module[];
  detail: (module: Module) => string;
}): React.ReactElement {
  return (
    <ValueList
      items={modules.map((module) => ({
        id: module.id,
        title: module.title,
        detail: `${module.code} · ${detail(module)}`,
      }))}
      empty="No modules recorded in this version."
    />
  );
}

export function StructuredValue({ value }: { value: unknown }): React.ReactElement {
  if (value === undefined || value === null || value === "") {
    return <MutedText as="span">Not set</MutedText>;
  }
  if (Array.isArray(value)) {
    return <span>{value.length} recorded</span>;
  }
  if (typeof value === "object") {
    const fields = Object.keys(value).length;
    return (
      <span>
        {fields} {fields === 1 ? "field" : "fields"} recorded
      </span>
    );
  }
  return <span>{String(value)}</span>;
}
